import { Component, OnInit, TemplateRef } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { PaginationResult } from 'src/app/services/abstract/abstract.service';
import { CompanyService } from 'src/app/services/company/company.service';
import { FileMediaService } from 'src/app/services/file-media/file-media.service';
import { PRODUCT_TYPE, ProductService } from 'src/app/services/product/product.service';
import { moveItemToNewPosition } from 'src/app/utilities/array';
import { Company, Offer, Product, ProviderDatacenterCompanyPool, ProviderResourceCompanyPool, CompanyPaymentType as _CompanyPaymentType } from 'src/app/models/verylys';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CompanyConfig } from 'src/config';
import { PaymentType } from 'src/app/services/cart/cart.service';

interface CompanyPaymentType extends _CompanyPaymentType {
    logo: string | SafeResourceUrl;
}

interface SocialLink {
    url: string;
    icon: string;
}

enum DYNAMIC_DATA {
    GAMES = "games",
    DATACENTERS = "datacenters",
    COUNTRIES = "countries"
}

@Component({
    selector: 'app-footer',
    templateUrl: './footer.component.html',
    styleUrls: ['./footer.component.scss']
})
export class FooterComponent implements OnInit {
    public products: { game_servers: Product[], voice_servers: Product[], others: Product[] } = {
        game_servers: [],
        voice_servers: [],
        others: []
    }

    public DYNAMIC_DATA: typeof DYNAMIC_DATA = DYNAMIC_DATA;
    public dynamicDatas: any[DYNAMIC_DATA] = [];
    private dynamicDatasIncrementation: any[DYNAMIC_DATA] = [];
    public paymentTypes: CompanyPaymentType[] = [];
    private destroy$: Subject<void> = new Subject();
    public socialLinks: SocialLink[] = [
        {
            icon: "fa-brands fa-discord",
            url: environment.discord_url
        },
        {
            icon: "fa-brands fa-square-facebook",
            url: environment.facebook_url
        },
        {
            icon: "fa-brands fa-x-twitter",
            url: environment.twitter_url
        },
        {
            icon: "fa-brands fa-instagram",
            url: environment.instagram_url
        }
    ];

    get year(): any {
        return new Date().getFullYear();
    }

    constructor(private router: Router,
        private companyService: CompanyService,
        private productService: ProductService,
        private fileMediaService: FileMediaService) { }

    async ngOnInit(): Promise<void> {
        this.loadProducts();
        this.loadPaymentTypes();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();

        for (const [key, value] of Object.keys(this.dynamicDatasIncrementation)) {
            if (this.dynamicDatasIncrementation[key]) {
                clearInterval(this.dynamicDatasIncrementation[key])
            }
        }
    }

    protected seoUrl(product: Product): string {
        return this.productService.seoUrl(product);
    }

    /**
     * Load the payment types
     */
    private async loadPaymentTypes(): Promise<void> {
        this.companyService.get(CompanyConfig.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe((result: Company) => {
                this.paymentTypes = result.payment_types as CompanyPaymentType[];
                this.paymentTypes = this.paymentTypes.filter((p) => p.name != 'Alpha Free');

                // Move payment method to first & second position
                moveItemToNewPosition(this.paymentTypes, this.paymentTypes.findIndex((paymentType: any) => paymentType?.label == "bank_card"), 0);
                moveItemToNewPosition(this.paymentTypes, this.paymentTypes.findIndex((paymentType: any) => paymentType?.label == "paypal"), 1);
            });
    }

    /** Load the products */
    private async loadProducts(): Promise<void> {
        this.productService.getDetailedList()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result: PaginationResult<Product>) => {
                const products: Product[] = result.results?.filter((product: Product) => this.productService.isDisplayed(product));

                this.products.game_servers = this.productService.getProductsByType(products, PRODUCT_TYPE.GAME_SERVER);
                this.products.voice_servers = this.productService.getProductsByType(products, PRODUCT_TYPE.VOICE_SERVER);

                let providerDatacenterCompanyPools: ProviderDatacenterCompanyPool[] = [];

                // Get all the provider datacetner company pool of all the offers products
                for (let product of products) {
                    let offers: Offer[] = product.offers as Offer[];

                    if (!offers?.length) {
                        continue;
                    }

                    offers.map((offer: Offer) => {
                        const providerResourceCompanyPool: any /*ProviderResourceCompanyPool*/ = offer.provider_resource_company_pool as ProviderResourceCompanyPool;
                        providerDatacenterCompanyPools = providerDatacenterCompanyPools.concat((providerResourceCompanyPool?.provider_datacenter_company_pools as ProviderDatacenterCompanyPool[]));
                    });
                }

                // and remove the dulication
                providerDatacenterCompanyPools = _.uniqBy(providerDatacenterCompanyPools, "_id")

                this.setDynamicDataIncrementationInterval(DYNAMIC_DATA.GAMES, this.products?.game_servers?.length);
                this.setDynamicDataIncrementationInterval(DYNAMIC_DATA.DATACENTERS, providerDatacenterCompanyPools?.length);
                this.setDynamicDataIncrementationInterval(DYNAMIC_DATA.COUNTRIES, 15);

                this.products.others = [
                    ...this.productService.getProductsByType(products, PRODUCT_TYPE.CODE),
                    ...this.productService.getProductsByType(products, PRODUCT_TYPE.VIRTUAL_CURRENCY),
                ]
            });
    }

    /**
     * Set an incrementation interval to a dynamic data
     */

    private setDynamicDataIncrementationInterval(dynamicData: DYNAMIC_DATA, max: number) {
        if (!max) {
            return;
        }

        if (this.dynamicDatasIncrementation[dynamicData]) {
            clearInterval(this.dynamicDatasIncrementation[dynamicData]);
        }

        if (!this.dynamicDatas[dynamicData]) {
            this.dynamicDatas[dynamicData] = 0;
        }

        this.dynamicDatasIncrementation[dynamicData] = setInterval(() => {
            this.dynamicDatas[dynamicData] += 1;

            if (this.dynamicDatas[dynamicData] >= max) {
                clearInterval(this.dynamicDatasIncrementation[dynamicData]);
                this.dynamicDatasIncrementation[dynamicData] = null;
            }
        }, 25);
    }
}
